import { useEffect, useState } from 'react'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { Alert, AlertType } from 'components/Alert/Alert'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import useModalClose from 'hooks/useModalClose'
import { ColorVariant, CursorPointer, Flex, colors } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import {
  ICreateFoodManualAdjustmentRequest,
  IFoodDietaryTagResponse,
  IFoodKitchenWindowSummaryResponse,
  IFoodWindowSummaryResponse,
  FoodMealWindowRunStatus,
} from 'types/api/foodApi.interface'

interface IAdjustLine {
  kitchenId: number
  tagCode: string
  qty: string
}

interface IProps {
  window: IFoodWindowSummaryResponse
  dietaryTags: IFoodDietaryTagResponse[]
  closeModal: () => void
  onSubmit: (body: ICreateFoodManualAdjustmentRequest) => void
}

export const AdjustPlanModal = ({ window, dietaryTags, closeModal, onSubmit }: IProps) => {
  const kitchens = window.kitchens || []
  const firstKitchenId = kitchens[0]?.kitchen?.id
  const firstTag = dietaryTags[0]?.code || ''
  const [adjLines, setAdjLines] = useState<IAdjustLine[]>([
    { kitchenId: firstKitchenId, tagCode: firstTag, qty: '' },
  ])
  const [reason, setReason] = useState('')
  useModalClose(closeModal)

  useEffect(() => {
    if (firstKitchenId) {
      setAdjLines([{ kitchenId: firstKitchenId, tagCode: firstTag, qty: '' }])
    }
  }, [firstKitchenId, firstTag])

  const tagOptions = dietaryTags.map((t) => ({ label: t.name || t.code, value: t.code }))
  const kitchenOptions = kitchens
    .filter((k) => k.kitchen)
    .map((k) => ({ label: k.kitchen.name, value: k.kitchen.id }))

  const kitchenCaps = (row: IFoodKitchenWindowSummaryResponse) =>
    (row.kitchen?.dietaryTagCapabilities || []).map((t) => t.code)

  const dietBad = adjLines.find((l) => {
    const k = kitchens.find((x) => x.kitchen?.id === Number(l.kitchenId))
    if (!k || !l.tagCode) return false
    const caps = kitchenCaps(k)
    return caps.length > 0 && !caps.includes(l.tagCode)
  })

  const locked = window.status !== FoodMealWindowRunStatus.Open && window.status !== null
  const valid =
    reason.trim() &&
    adjLines.every((l) => Number(l.qty) > 0 && l.kitchenId && l.tagCode) &&
    !dietBad

  const handleSubmit = () => {
    onSubmit({
      reason: reason.trim(),
      lines: adjLines.map((l) => ({
        kitchenId: Number(l.kitchenId),
        dietaryTagCodes: l.tagCode ? [l.tagCode] : [],
        qty: Number(l.qty),
      })),
    })
  }

  return (
    <Modal black width='560px'>
      <Flex column gap='1rem' width='100%'>
        <Flex between width='100%' center>
          <TitleText weight='bold'>Adjust Plan — {window.windowDef?.name}</TitleText>
          <CursorPointer onClick={closeModal}>
            <CloseIcon />
          </CursorPointer>
        </Flex>
        <OtherText grey>
          Derived plan: {window.planned}
          {window.manualAdjustmentQty > 0 && ` · existing manual: +${window.manualAdjustmentQty}`}
        </OtherText>
        {locked ? (
          <Alert
            title='Cut-off has passed'
            description='Hot orders are locked per kitchen — these quantities will be routed to standby reserve at the selected kitchens instead of increasing hot orders.'
            type={AlertType.WARNING}
            colorVariant={ColorVariant.PRIMARY}
          />
        ) : (
          <Alert
            title='Manual adjustment'
            description='Increases create MANUAL_ADJUSTMENT intent records (per diet, per kitchen). They survive schedule regeneration and are excluded from show-rate learning.'
            type={AlertType.INFO}
            colorVariant={ColorVariant.SECONDARY}
          />
        )}
        <BodyText weight='600'>Additional meals — per kitchen, per dietary tag</BodyText>
        {adjLines.map((l, i) => {
          const k = kitchens.find((x) => x.kitchen?.id === Number(l.kitchenId))
          const incompatible =
            k && l.tagCode && kitchenCaps(k).length > 0 && !kitchenCaps(k).includes(l.tagCode)
          return (
            <Flex column gap='0.25rem' key={i} width='100%'>
              <Flex gap='0.5rem' width='100%' alignEnd>
                <HonestSelectInput
                  width='100%'
                  options={kitchenOptions}
                  defaultValue={l.kitchenId}
                  onChange={(v: any) =>
                    setAdjLines(
                      adjLines.map((x, j) => (j === i ? { ...x, kitchenId: Number(v.value) } : x)),
                    )
                  }
                  name='kitchenId'
                  labelText='Kitchen'
                  nomarg
                />
                <HonestSelectInput
                  width='100%'
                  options={tagOptions}
                  defaultValue={l.tagCode}
                  onChange={(v: any) =>
                    setAdjLines(adjLines.map((x, j) => (j === i ? { ...x, tagCode: v.value } : x)))
                  }
                  name='tag'
                  labelText='Dietary'
                  nomarg
                />
                <Input
                  type='number'
                  placeholder='Quantity'
                  value={l.qty}
                  onChange={(e: any) =>
                    setAdjLines(
                      adjLines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)),
                    )
                  }
                  labelText='Quantity'
                  nomarg
                />
                {adjLines.length > 1 && (
                  <CursorPointer onClick={() => setAdjLines(adjLines.filter((_, j) => j !== i))}>
                    <CloseIcon />
                  </CursorPointer>
                )}
              </Flex>
              {incompatible && (
                <OtherText color={colors.red}>
                  Dietary hard stop: {k!.kitchen.name} does not cover {l.tagCode}
                </OtherText>
              )}
            </Flex>
          )
        })}
        <Button
          variant='secondary'
          size='sm'
          onClick={() =>
            setAdjLines([...adjLines, { kitchenId: firstKitchenId, tagCode: firstTag, qty: '' }])
          }
        >
          Add line
        </Button>
        <OtherText grey>
          Reducing the plan? Don&apos;t use this — select people in the Employee List and use Cancel
          Selected.
        </OtherText>
        <Input
          type='textarea'
          labelText='Reason (mandatory — shown on the window card)'
          value={reason}
          onChange={(e: any) => setReason(e.target.value)}
          placeholder='e.g. Client delegation arriving for lunch'
          nomarg
        />
        <Flex gap='0.5rem' justcenter width='100%'>
          <Button variant='secondary' onClick={closeModal}>
            Cancel
          </Button>
          <Button variant='primary' disabled={!valid} onClick={handleSubmit}>
            Submit
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}
